import React from 'react';
import { Card, Form, Input, DatePicker, Select, Space, Typography, message, Table, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetVwConstMachines, useGetCompanies, useGetConstMachineAllocations } from '../../../hooks/genericHooks';
import { useQueryClient } from '@tanstack/react-query';
import type { VwConstMachines, Company, ConstMachineAllocation } from '../../../types';
import { generateConstMachineAllocationRefNum, insertData } from '../../../services/api';
import PersonnelSelect from '../../../components/modals/PersonnelSelect';
import WPermitSelect from '../../../components/modals/WPermitSelect';
import ActivitySelections from '../../../components/modals/ActivitySelections';
import { taskService } from '../../../components/common/TaskManagement/taskService';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface AllocationFormValues {
  ref_num: string;
  machine_id: number;
  company_id?: number;
  requester_personnel_id: number;
  requester_display: string;
  approver_personnel_id?: number;
  approver_display?: string;
  location: string;
  description: string;
  start_time: Dayjs;
  end_time: Dayjs;
}

const MachMachineAllocationCreate: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AllocationFormValues>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: machinesData } = useGetVwConstMachines();
  const { data: companiesData } = useGetCompanies();
  const { data: allocationsData } = useGetConstMachineAllocations();

  const machines: VwConstMachines[] = Array.isArray(machinesData)
    ? (machinesData as any)
    : (machinesData?.data || []);
  const companies: Company[] = Array.isArray(companiesData)
    ? (companiesData as any)
    : (companiesData?.data || []);
  const allocations: ConstMachineAllocation[] = Array.isArray(allocationsData)
    ? (allocationsData as any)
    : (allocationsData?.data || []);

  const [submitting, setSubmitting] = React.useState(false);

  // Personnel & approver modals
  const [requesterModalOpen, setRequesterModalOpen] = React.useState(false);
  const [approverModalOpen, setApproverModalOpen] = React.useState(false);

  // Work Permit / Activity modals & state
  const [wpOpen, setWpOpen] = React.useState(false);
  const [wpRefNum, setWpRefNum] = React.useState<string>('');
  const [wpDesc, setWpDesc] = React.useState<string>('');
  const [wpActCode, setWpActCode] = React.useState<string>('');
  const [wpActName, setWpActName] = React.useState<string>('');

  const [actOpen, setActOpen] = React.useState(false);
  const [actCombined, setActCombined] = React.useState<string>('');
  const [actCode, setActCode] = React.useState<string>('');
  const [actName, setActName] = React.useState<string>('');

  const finalActCodeDisplay = wpRefNum ? (wpActCode || '') : (actCode || '');
  const finalActNameDisplay = wpRefNum ? (wpActName || '') : (actName || '');

  // Seçili makine id'sini izle
  const selectedMachineId = Form.useWatch('machine_id', form) as number | undefined;

  // Seçili makine için aktif rezervasyonlar (requested/approved/deferred)
  const machineAllocations = React.useMemo(() => {
    if (!selectedMachineId) return [] as ConstMachineAllocation[];
    const activeStatuses: ConstMachineAllocation['status'][] = ['requested', 'approved', 'deferred'];
    return (allocations || []).filter(
      (a) =>
        a.machine_id === selectedMachineId &&
        activeStatuses.includes(a.status)
    );
  }, [allocations, selectedMachineId]);

  // Prefill ref_num and default times
  React.useEffect(() => {
    (async () => {
      try {
        const ref = await generateConstMachineAllocationRefNum();
        form.setFieldsValue({
          ref_num: ref,
          start_time: dayjs(),
          end_time: dayjs().add(4, 'hour'),
        } as any);
      } catch {
        form.setFieldsValue({
          start_time: dayjs(),
          end_time: dayjs().add(4, 'hour'),
        } as any);
      }
    })();
  }, [form]);

  const machineOptions = React.useMemo(
    () =>
      (machines || []).map((m) => {
        let specsStr = '';
        if (m.specs && typeof m.specs === 'object') {
          const entries = Object.entries(m.specs).filter(
            ([k, v]) => k && v != null && String(v).trim() !== ''
          );
          if (entries.length) {
            specsStr = entries
              .map(([k, v]) => `${k}: ${String(v)}`)
              .join('; ');
          }
        }
        const baseModel =
          `${m.make || ''} ${m.model || ''}`.trim() || `ID ${m.id}`;
        const label = specsStr ? `${baseModel} – ${specsStr}` : baseModel;
        return {
          value: m.id,
          label,
        };
      }),
    [machines]
  );

  const companyOptions = React.useMemo(
    () =>
      (companies || [])
        .filter((c) => (c as any).short_code)
        .map((c: any) => ({
          value: c.id,
          label: `${c.short_code} - ${c.name}`,
        })),
    [companies]
  );

  const validateActivity = () => {
    if (!wpRefNum && !actCode) {
      message.warning(t('machinery.allocations.selectWpOrActivity'));
      return false;
    }
    if (!finalActCodeDisplay || !finalActNameDisplay) {
      message.warning(t('machinery.allocations.activityMissing'));
      return false;
    }
    return true;
  };

  // Frontend tarafında overlap kontrolü (DB constraint ile aynı mantık)
  const hasOverlapForSelectedMachine = (startIso: string, endIso: string): boolean => {
    if (!selectedMachineId) return false;
    const start = dayjs(startIso);
    const end = dayjs(endIso);
    if (!start.isValid() || !end.isValid()) return false;

    return machineAllocations.some((a) => {
      if (!a.start_time || !a.end_time) return false;
      const s2 = dayjs(a.start_time);
      const e2 = dayjs(a.end_time);
      if (!s2.isValid() || !e2.isValid()) return false;
      // Overlap: start < e2 && end > s2  (DB'deki '[)' mantığı ile uyumlu)
      return start.isBefore(e2) && end.isAfter(s2);
    });
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      const values = form.getFieldsValue();

      if (!validateActivity()) {
        return;
      }

      setSubmitting(true);

      const startIso = (values.start_time as Dayjs).toISOString();
      const endIso = (values.end_time as Dayjs).toISOString();

      // 0) Frontend overlap kontrolü: DB'ye gitmeden önce engelle
      if (hasOverlapForSelectedMachine(startIso, endIso)) {
        message.error(t('machinery.allocations.overlapFrontend'));
        return;
      }

      const payload: Partial<ConstMachineAllocation> = {
        ref_num: values.ref_num,
        machine_id: Number(values.machine_id),
        requester_personnel_id: Number(values.requester_personnel_id),
        approver_personnel_id: values.approver_personnel_id ? Number(values.approver_personnel_id) : null,
        company_id: values.company_id ? Number(values.company_id) : null,
        location: values.location,
        description: values.description,
        workpermit_ref_num: wpRefNum || null,
        activity_code: finalActCodeDisplay,
        activity_name: finalActNameDisplay,
        start_time: startIso,
        end_time: endIso,
        // status alanini gondermiyoruz; veritabanindaki DEFAULT 'requested' degerini kullansin
      };

      const resp = await insertData('const_machine_allocation', payload);
      // insertData dbExchange/insert su sekilde donuyor: { ids: [<id>], success: true, message: ... }
      const ids = (resp?.ids as Array<number | string> | undefined) || [];
      const newId = ids.length ? Number(ids[0]) : undefined;
      if (!newId) {
        throw new Error(t('machinery.allocations.noIdReturned'));
      }

      // Create assigned task for approver if provided
      if (values.approver_personnel_id) {
        // approver_display: "PERSID - Full Name" formatından personnel_id (PERSID) çıkar
        let approverPid: string | undefined;
        if (values.approver_display) {
          const disp = String(values.approver_display);
          const idx = disp.indexOf(' - ');
          approverPid = (idx >= 0 ? disp.slice(0, idx) : disp).trim();
        }

        if (approverPid) {
          const machine = (machines || []).find((m) => m.id === Number(values.machine_id));
          const machineLabel =
            (machine?.license_plate || '').trim() ||
            `${machine?.make || ''} ${machine?.model || ''}`.trim() ||
            `ID ${values.machine_id}`;

          const desc = `Machine allocation ${values.ref_num} - ${machineLabel}`;

          await taskService.assignAction({
            entity: { kind: 'machinery.machine_allocation', id: newId },
            assignees: [approverPid],
            navigateTo: `/machinery/allocations/view/${newId}`,
            description: desc,
            tag: 'approve',
            clientKey: `machinery:machine_allocation:${newId}:approve`,
            fanoutPerAssignee: false,
          });
        }
      }

      message.success(t('machinery.allocations.createSuccess'));
      // Listeyi dinleyen hook'lara haber ver (event-based refresh)
      try {
        window.dispatchEvent(new CustomEvent('const_machine_allocation:changed'));
      } catch {}
      // After creating, go back to allocations list instead of detail page
      navigate('/machinery/allocations');
    } catch (e: any) {
      if (e?.errorFields) {
        return;
      }
      console.error(e);
      const rawMsg: string | undefined =
        e?.response?.data?.error || e?.message || '';
      if (rawMsg && rawMsg.includes('const_machine_allocation_no_overlap')) {
        message.error(t('machinery.allocations.overlapDb'));
      } else {
        message.error(rawMsg || t('machinery.allocations.createFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card style={{ margin: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('machinery.allocations.createTitle')}
        </Title>
      </div>

      <Form<AllocationFormValues>
        form={form}
        layout="vertical"
        style={{ maxWidth: 960, margin: '0 auto', width: '100%' }}
      >
        {/* Üst satır: Ref # ve tarih alanları */}
        <Space wrap style={{ width: '100%' }} size={[8, 8]}>
          <Form.Item name="ref_num" label="Ref #" rules={[{ required: true }]} style={{ width: 160 }}>
            <Input readOnly disabled />
          </Form.Item>
          <Form.Item
            name="start_time"
            label={t('machinery.allocations.startTime')}
            rules={[{ required: true, message: t('machinery.allocations.startTimeRequired') }]}
            style={{ minWidth: 200 }}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
              onChange={(d) => {
                // Form alanlarini hem start hem end icin guncelle
                if (d) {
                  const end = d.add(30, 'minute');
                  form.setFieldsValue({
                    start_time: d,
                    end_time: end,
                  } as any);
                } else {
                  form.setFieldsValue({
                    start_time: null,
                    end_time: null,
                  } as any);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="end_time"
            label={t('machinery.allocations.endTime')}
            rules={[{ required: true, message: t('machinery.allocations.endTimeRequired') }]}
            style={{ minWidth: 200 }}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Space>

        {/* İkinci satır: Makine ve firma seçimi */}
        <Space wrap style={{ width: '100%', marginTop: 8 }} size={[8, 8]}>
          <Form.Item
            name="machine_id"
            label={t('machinery.allocations.machine')}
            rules={[{ required: true, message: t('machinery.allocations.machineRequired') }]}
            style={{ minWidth: 520, flex: 1 }}
          >
            <Select
              showSearch
              placeholder={t('machinery.allocations.machinePlaceholder')}
              options={machineOptions}
              optionFilterProp="label"
              dropdownMatchSelectWidth={false}
            />
          </Form.Item>

          <Form.Item
            name="company_id"
            label={t('machinery.allocations.company')}
            rules={[{ required: true, message: t('machinery.allocations.companyRequired') }]}
            style={{ minWidth: 240 }}
          >
            <Select
              showSearch
              placeholder={t('machinery.allocations.companyPlaceholder')}
              options={companyOptions}
              optionFilterProp="label"
              allowClear
            />
          </Form.Item>
        </Space>

        {/* Selected machine existing reservations (directly under Machine field) */}
        <Card size="small" style={{ marginTop: 8 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Text strong>{t('machinery.allocations.existingTitle')}</Text>
            {!selectedMachineId && (
              <Text type="secondary">{t('machinery.allocations.existingHint')}</Text>
            )}
            {selectedMachineId && machineAllocations.length === 0 && (
              <Text type="secondary">{t('machinery.allocations.existingEmpty')}</Text>
            )}
            {selectedMachineId && machineAllocations.length > 0 && (
              <Table<ConstMachineAllocation>
                size="small"
                rowKey={(r) => String(r.id)}
                dataSource={machineAllocations}
                pagination={false}
                columns={[
                  { title: 'Ref #', dataIndex: 'ref_num', key: 'ref_num', width: 140 },
                  {
                    title: t('machinery.allocations.colStart'),
                    dataIndex: 'start_time',
                    key: 'start_time',
                    render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
                  },
                  {
                    title: t('machinery.allocations.colEnd'),
                    dataIndex: 'end_time',
                    key: 'end_time',
                    render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
                  },
                  {
                    title: t('machinery.allocations.colStatus'),
                    dataIndex: 'status',
                    key: 'status',
                    width: 120,
                    render: (s: ConstMachineAllocation['status']) => {
                      let color: string = 'default';
                      if (s === 'requested') color = 'blue';
                      else if (s === 'approved') color = 'green';
                      else if (s === 'deferred') color = 'orange';
                      else if (s === 'rejected') color = 'red';
                      else if (s === 'cancelled') color = 'volcano';
                      else if (s === 'completed') color = 'geekblue';
                      return <Tag color={color}>{String(s).toUpperCase()}</Tag>;
                    },
                  },
                ]}
              />
            )}
          </Space>
        </Card>

        {/* Requester / Approver seçimleri (ID alanları gizli, sadece isim alanları görünür) */}
        <Form.Item
          name="requester_personnel_id"
          rules={[{ required: true, message: t('machinery.allocations.requesterRequired') }]}
          hidden
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="approver_personnel_id"
          rules={[{ required: true, message: t('machinery.allocations.approverRequired') }]}
          hidden
        >
          <Input />
        </Form.Item>

        <Space wrap style={{ width: '100%', marginTop: 8 }} size={[8, 8]}>
          <Form.Item
            name="requester_display"
            label={t('machinery.allocations.requester')}
            style={{ minWidth: 260, flex: 1 }}
          >
            <Input
              readOnly
              placeholder={t('machinery.allocations.requesterPlaceholder')}
              onClick={() => setRequesterModalOpen(true)}
            />
          </Form.Item>

          <Form.Item
            name="approver_display"
            label={t('machinery.allocations.approver')}
            style={{ minWidth: 260, flex: 1 }}
          >
            <Input
              readOnly
              placeholder={t('machinery.allocations.approverPlaceholder')}
              onClick={() => setApproverModalOpen(true)}
            />
          </Form.Item>
        </Space>

        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={8}>
          <Form.Item
            name="location"
            label={t('machinery.allocations.location')}
            rules={[{ required: true, message: t('machinery.allocations.locationRequired') }]}
          >
            <Input placeholder={t('machinery.allocations.location')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('machinery.allocations.description')}
            rules={[{ required: true, message: t('machinery.allocations.descriptionRequired') }]}
          >
            <TextArea rows={3} placeholder={t('machinery.allocations.descriptionPlaceholder')} />
          </Form.Item>
        </Space>

        {/* Work Permit / Activity selection */}
        <Card size="small" style={{ marginTop: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Text strong>{t('machinery.allocations.wpActivityTitle')}</Text>

            {/* Work Permit satırı */}
            <Space align="start" style={{ width: '100%' }}>
              <Button onClick={() => setWpOpen(true)}>{t('machinery.allocations.pickWorkPermit')}</Button>
              <Input
                placeholder={t('machinery.allocations.wpPlaceholder')}
                value={wpRefNum ? `${wpRefNum} - ${wpDesc}` : ''}
                readOnly
                style={{ flex: 1 }}
              />
              {wpRefNum && (
                <Button
                  onClick={() => {
                    setWpRefNum('');
                    setWpDesc('');
                    setWpActCode('');
                    setWpActName('');
                  }}
                  danger
                >
                  {t('machinery.allocations.clear')}
                </Button>
              )}
            </Space>

            {/* Activity satırı */}
            <Space align="start" style={{ width: '100%' }}>
              <Button onClick={() => setActOpen(true)} disabled={!!wpRefNum}>
                {t('machinery.allocations.pickActivity')}
              </Button>
              <Input
                placeholder={t('machinery.allocations.activityCombinedPlaceholder')}
                value={actCombined || (finalActCodeDisplay ? `${finalActCodeDisplay} - ${finalActNameDisplay}` : '')}
                readOnly
                style={{ flex: 1 }}
              />
              {actCode && !wpRefNum && (
                <Button
                  onClick={() => {
                    setActCombined('');
                    setActCode('');
                    setActName('');
                  }}
                  danger
                >
                  {t('machinery.allocations.clear')}
                </Button>
              )}
            </Space>

            {/* Seçili activity detayları */}
            <Space wrap style={{ width: '100%' }}>
              <Input
                placeholder={t('machinery.allocations.activityCode')}
                value={finalActCodeDisplay}
                readOnly
                status={finalActCodeDisplay ? undefined : 'error'}
                style={{ width: 200 }}
              />
              <Input
                placeholder={t('machinery.allocations.activityName')}
                value={finalActNameDisplay}
                readOnly
                status={finalActNameDisplay ? undefined : 'error'}
                style={{ flex: 1, minWidth: 260 }}
              />
            </Space>
          </Space>
        </Card>

        <Space style={{ marginTop: 16, justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={() => navigate(-1)}>{t('machinery.allocations.cancel')}</Button>
          <Button action="add" type="primary" onClick={handleSubmit} loading={submitting}>
            {t('machinery.allocations.createReservation')}
          </Button>
        </Space>
      </Form>

      {/* Personnel modals */}
      <PersonnelSelect
        open={requesterModalOpen}
        mode="extended"
        onCancel={() => setRequesterModalOpen(false)}
        onOk={(payload: any) => {
          try {
            let code: string | undefined;
            let fullName: string | undefined;
            let numericId: number | undefined;
            if (payload && Array.isArray(payload) && payload.length >= 4) {
              // modes.both or extended style
              const [, codeStr, nameStr, numId] = payload;
              code = String(codeStr || '');
              fullName = String(nameStr || '');
              numericId = typeof numId === 'number' ? numId : Number(numId);
            } else if (payload && payload.modes && payload.modes.extended) {
              const ext = payload.modes.extended as any[];
              const [, codeStr, nameStr, numId] = ext;
              code = String(codeStr || '');
              fullName = String(nameStr || '');
              numericId = typeof numId === 'number' ? numId : Number(numId);
            }
            if (numericId && code && fullName) {
              form.setFieldsValue({
                requester_personnel_id: numericId,
                requester_display: `${code} - ${fullName}`,
              } as any);
            }
          } catch {
            // no-op
          }
          setRequesterModalOpen(false);
        }}
      />

      <PersonnelSelect
        open={approverModalOpen}
        mode="extended"
        onCancel={() => setApproverModalOpen(false)}
        onOk={(payload: any) => {
          try {
            let code: string | undefined;
            let fullName: string | undefined;
            let numericId: number | undefined;
            if (payload && Array.isArray(payload) && payload.length >= 4) {
              const [, codeStr, nameStr, numId] = payload;
              code = String(codeStr || '');
              fullName = String(nameStr || '');
              numericId = typeof numId === 'number' ? numId : Number(numId);
            } else if (payload && payload.modes && payload.modes.extended) {
              const ext = payload.modes.extended as any[];
              const [, codeStr, nameStr, numId] = ext;
              code = String(codeStr || '');
              fullName = String(nameStr || '');
              numericId = typeof numId === 'number' ? numId : Number(numId);
            }
            if (numericId && code && fullName) {
              form.setFieldsValue({
                approver_personnel_id: numericId,
                approver_display: `${code} - ${fullName}`,
              } as any);
            }
          } catch {
            // no-op
          }
          setApproverModalOpen(false);
        }}
      />

      {/* Work Permit & Activity modals */}
      <WPermitSelect
        open={wpOpen}
        onCancel={() => setWpOpen(false)}
        onOk={([ref, desc, aCode, aName]) => {
          setWpRefNum(ref);
          setWpDesc(desc);
          setWpActCode(aCode);
          setWpActName(aName);
          setActCombined(`${aCode} - ${aName}`);
          setActCode(aCode);
          setActName(aName);
          setWpOpen(false);
        }}
      />

      <ActivitySelections
        open={actOpen}
        onCancel={() => setActOpen(false)}
        onOk={(sel) => {
          if (Array.isArray(sel)) {
            const [combined, code, name] = sel as [string, string, string, number?];
            setActCombined(combined || `${code} - ${name}`);
            setActCode(code);
            setActName(name);
          } else if (typeof sel === 'string') {
            setActCombined(sel);
            setActCode(sel);
            setActName('');
          }
          setWpRefNum('');
          setWpDesc('');
          setWpActCode('');
          setWpActName('');
          setActOpen(false);
        }}
      />
    </Card>
  );
};

export default MachMachineAllocationCreate;

